import { FC, useCallback, useEffect, useState } from 'react'

import { VStack } from '@revolut-internal/ui-kit'

import { PageSection } from 'components/PageSection/PageSection'
import { useChannel } from 'hooks/useChannel'
import { useQueuesHealthDetailsQuery } from 'modules/api/analytics/queries'
import { UrlStateStorageKeys } from 'modules/url-state-storage/consts'
import { useHashedSearchParams } from 'modules/url-state-storage/useHashedSearchParams'
import { useAdditionalFilters } from 'pages/Monitoring/GlobalFilters/AdditionalFilters/AdditionalFiltersContext'
import { GlobalFilterState } from 'pages/Monitoring/GlobalFilters/GlobalFilters'
import { useTableRowHeight } from 'providers/TableRowHeightProvider'
import { ResourcesSideParams, TicketsFilterType } from 'types'

import { TableViewPicker } from './components'
import { QueueHealthResourcesSide } from './QueueHealthResourcesSide'
import { QueuesHealthDetailsMetrics } from './QueuesHealthDetailsMetrics'
import { QueuesHealthDetailsTable } from './QueuesHealthDetailsTable'

export const QueuesHealthDetails: FC = () => {
  const [resourcesSideParams, setResourcesSideParams] = useState<
    ResourcesSideParams | undefined
  >(undefined)

  const { preparedFiltersForRequest } = useAdditionalFilters()
  const { activeTableRowHeight, setActiveTableRowHeight } = useTableRowHeight()
  const { channel, regionedChannel } = useChannel()

  const { state } = useHashedSearchParams<GlobalFilterState>({
    contextKey: regionedChannel,
    hashParamKey: UrlStateStorageKeys.GlobalFilters,
  })

  const {
    data: queuesHealthDetails,
    status,
    isPending,
  } = useQueuesHealthDetailsQuery({
    departmentId: state.departments,
    areaId: state.areas,
    specialisationId: state.specialisations,
    queueId: state.queues,
    channel: channel ? [channel] : [],
    filter: preparedFiltersForRequest,
  })

  useEffect(() => {
    if (channel) {
      setResourcesSideParams(undefined)
    }
  }, [channel])

  const handleResourceChange = useCallback((value: string) => {
    setResourcesSideParams(prevState => ({
      ...prevState!,
      filterType: value as TicketsFilterType,
    }))
  }, [])

  return (
    <PageSection
      title="Queue health breakdown"
      side={
        <TableViewPicker value={activeTableRowHeight} onClick={setActiveTableRowHeight} />
      }
    >
      <VStack space="s-16">
        <QueuesHealthDetailsMetrics
          metrics={queuesHealthDetails?.metrics}
          isPending={isPending}
        />

        <QueuesHealthDetailsTable
          data={queuesHealthDetails?.specialisations ?? []}
          metrics={queuesHealthDetails?.metrics}
          queryStatus={status}
          resourcesSideParams={resourcesSideParams}
          onShowResources={setResourcesSideParams}
        />
      </VStack>

      <QueueHealthResourcesSide
        queueHealthSpecialisations={queuesHealthDetails?.specialisations}
        resourcesSideParams={resourcesSideParams}
        onResourceChange={handleResourceChange}
        onClose={() => setResourcesSideParams(undefined)}
      />
    </PageSection>
  )
}
